/**
 * Audit Logger Service for GDPR compliance
 * Tracks all access to sensitive medical data
 */

import logger from './Logger';

export enum AuditEventType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  VIEW_REPORT = 'VIEW_REPORT',
  DOWNLOAD_REPORT = 'DOWNLOAD_REPORT',
  VIEW_DICOM = 'VIEW_DICOM',
  DOWNLOAD_DICOM = 'DOWNLOAD_DICOM',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  UPDATE_EMAIL = 'UPDATE_EMAIL',
  ACCESS_DENIED = 'ACCESS_DENIED',
  DATA_EXPORT = 'DATA_EXPORT',
  DATA_DELETION = 'DATA_DELETION',
  CONSENT_GIVEN = 'CONSENT_GIVEN',
  CONSENT_WITHDRAWN = 'CONSENT_WITHDRAWN'
}

export interface AuditLog {
  timestamp: string;
  eventType: AuditEventType;
  userId?: string;
  codiceFiscale?: string;
  ipAddress?: string;
  userAgent?: string;
  resourceId?: string;
  resourceType?: string;
  action: string;
  result: 'SUCCESS' | 'FAILURE';
  details?: Record<string, any>;
  gdprLawfulBasis?: 'consent' | 'contract' | 'legal_obligation' | 'vital_interests' | 'public_task' | 'legitimate_interests';
}

class AuditLogger {
  private static instance: AuditLogger;
  private logs: AuditLog[] = [];
  private batchSize = 100;
  private flushInterval = 60000; // 1 minute
  private flushTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.startFlushTimer();
    
    // Flush logs before page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  static getInstance(): AuditLogger {
    if (!AuditLogger.instance) {
      AuditLogger.instance = new AuditLogger();
    }
    return AuditLogger.instance;
  }

  /**
   * Log an audit event
   */
  log(event: Partial<AuditLog> & { eventType: AuditEventType; action: string; result: 'SUCCESS' | 'FAILURE' }): void {
    const auditLog: AuditLog = {
      timestamp: new Date().toISOString(),
      ipAddress: this.getClientIP(),
      userAgent: navigator.userAgent,
      ...event
    };

    // Add user info from session if available
    const token = localStorage.getItem('token');
    if (token && !auditLog.userId) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        auditLog.userId = payload.sub || payload.userId;
      } catch (error) {
        logger.error('Failed to parse token for audit log:', error);
      }
    }

    this.logs.push(auditLog);
    logger.debug('Audit event logged:', auditLog);

    // Check if batch is full
    if (this.logs.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Log successful login
   */
  logLogin(codiceFiscale: string): void {
    this.log({
      eventType: AuditEventType.LOGIN,
      action: 'User login',
      result: 'SUCCESS',
      codiceFiscale,
      gdprLawfulBasis: 'contract'
    });
  }

  /**
   * Log logout
   */
  logLogout(codiceFiscale?: string): void {
    this.log({
      eventType: AuditEventType.LOGOUT,
      action: 'User logout',
      result: 'SUCCESS',
      codiceFiscale
    });
  }

  /**
   * Log report view
   */
  logReportView(reportId: string, patientId: string): void {
    this.log({
      eventType: AuditEventType.VIEW_REPORT,
      action: 'View medical report',
      result: 'SUCCESS',
      resourceId: reportId,
      resourceType: 'REPORT',
      codiceFiscale: patientId,
      gdprLawfulBasis: 'contract',
      details: {
        reportId,
        viewedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log report download
   */
  logReportDownload(reportId: string, patientId: string): void {
    this.log({
      eventType: AuditEventType.DOWNLOAD_REPORT,
      action: 'Download medical report',
      result: 'SUCCESS',
      resourceId: reportId,
      resourceType: 'REPORT',
      codiceFiscale: patientId,
      gdprLawfulBasis: 'contract',
      details: {
        reportId,
        downloadedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log DICOM image view
   */
  logDicomView(studyUID: string, patientId: string): void {
    this.log({
      eventType: AuditEventType.VIEW_DICOM,
      action: 'View DICOM images',
      result: 'SUCCESS',
      resourceId: studyUID,
      resourceType: 'DICOM',
      codiceFiscale: patientId,
      gdprLawfulBasis: 'contract',
      details: {
        studyUID,
        viewedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log DICOM download
   */
  logDicomDownload(studyUID: string, patientId: string, jobId: string): void {
    this.log({
      eventType: AuditEventType.DOWNLOAD_DICOM,
      action: 'Download DICOM images',
      result: 'SUCCESS',
      resourceId: studyUID,
      resourceType: 'DICOM',
      codiceFiscale: patientId,
      gdprLawfulBasis: 'contract',
      details: {
        studyUID,
        jobId,
        downloadedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log access denied
   */
  logAccessDenied(resource: string, reason: string): void {
    this.log({
      eventType: AuditEventType.ACCESS_DENIED,
      action: `Access denied to ${resource}`,
      result: 'FAILURE',
      details: {
        resource,
        reason,
        deniedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log consent given
   */
  logConsentGiven(consentType: string, details: Record<string, any>): void {
    this.log({
      eventType: AuditEventType.CONSENT_GIVEN,
      action: `Consent given for ${consentType}`,
      result: 'SUCCESS',
      gdprLawfulBasis: 'consent',
      details: {
        consentType,
        ...details,
        consentGivenAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log consent withdrawn
   */
  logConsentWithdrawn(consentType: string, details: Record<string, any>): void {
    this.log({
      eventType: AuditEventType.CONSENT_WITHDRAWN,
      action: `Consent withdrawn for ${consentType}`,
      result: 'SUCCESS',
      details: {
        consentType,
        ...details,
        consentWithdrawnAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log data export (GDPR right to data portability)
   */
  logDataExport(userId: string, format: string): void {
    this.log({
      eventType: AuditEventType.DATA_EXPORT,
      action: 'Export personal data',
      result: 'SUCCESS',
      userId,
      gdprLawfulBasis: 'legal_obligation',
      details: {
        format,
        exportedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Log data deletion (GDPR right to erasure)
   */
  logDataDeletion(userId: string, dataType: string): void {
    this.log({
      eventType: AuditEventType.DATA_DELETION,
      action: 'Delete personal data',
      result: 'SUCCESS',
      userId,
      gdprLawfulBasis: 'legal_obligation',
      details: {
        dataType,
        deletedAt: new Date().toISOString()
      }
    });
  }

  /**
   * Get client IP address
   */
  private getClientIP(): string {
    // In production, this would come from the server
    // For now, we'll use a placeholder
    return 'CLIENT_IP';
  }

  /**
   * Send logs to backend
   */
  private async sendToBackend(logs: AuditLog[]): Promise<void> {
    // TEMPORANEAMENTE DISABILITATO - Backend endpoint non ancora disponibile
    logger.info(`[MOCK] Would send ${logs.length} audit logs to backend`);
    return;
    
    /*
    try {
      const response = await fetch('/api/audit/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ logs })
      });

      if (!response.ok) {
        throw new Error(`Failed to send audit logs: ${response.statusText}`);
      }

      logger.info(`Successfully sent ${logs.length} audit logs to backend`);
    } catch (error) {
      logger.error('Failed to send audit logs to backend:', error);
      // Store failed logs in localStorage for retry
      this.storeFailedLogs(logs);
    }
    */
  }

  /**
   * Store failed logs for retry
   */
  private storeFailedLogs(logs: AuditLog[]): void {
    try {
      const existingLogs = localStorage.getItem('failedAuditLogs');
      const failed = existingLogs ? JSON.parse(existingLogs) : [];
      failed.push(...logs);
      
      // Keep only last 1000 logs to prevent localStorage overflow
      const trimmed = failed.slice(-1000);
      localStorage.setItem('failedAuditLogs', JSON.stringify(trimmed));
    } catch (error) {
      logger.error('Failed to store audit logs in localStorage:', error);
    }
  }

  /**
   * Retry sending failed logs
   */
  private async retryFailedLogs(): Promise<void> {
    try {
      const failedLogs = localStorage.getItem('failedAuditLogs');
      if (failedLogs) {
        const logs = JSON.parse(failedLogs);
        if (logs.length > 0) {
          await this.sendToBackend(logs);
          localStorage.removeItem('failedAuditLogs');
        }
      }
    } catch (error) {
      logger.error('Failed to retry audit logs:', error);
    }
  }

  /**
   * Flush logs to backend
   */
  private async flush(): Promise<void> {
    if (this.logs.length === 0) return;

    const logsToSend = [...this.logs];
    this.logs = [];

    await this.sendToBackend(logsToSend);
    
    // Retry any previously failed logs
    await this.retryFailedLogs();
  }

  /**
   * Start flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  /**
   * Stop flush timer
   */
  stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Get audit logs for debugging (only in development)
   */
  getAuditLogs(): AuditLog[] {
    if (process.env.NODE_ENV === 'development') {
      return [...this.logs];
    }
    return [];
  }

  /**
   * Export audit logs as JSON (for GDPR data portability)
   */
  async exportUserAuditLogs(userId: string): Promise<Blob> {
    try {
      const response = await fetch(`/api/audit/export/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to export audit logs: ${response.statusText}`);
      }

      const data = await response.blob();
      this.logDataExport(userId, 'JSON');
      return data;
    } catch (error) {
      logger.error('Failed to export audit logs:', error);
      throw error;
    }
  }
}

// Export singleton instance
const auditLogger = AuditLogger.getInstance();
export default auditLogger;